// The close backstop for Birdie's bet submission (B27 item 10).
//
// The board arms its 1.5 s submit timer on the ARRIVAL of `race_starting`. On
// staging ~4 % of rounds see that row land more than a second late (p90 2.2 s,
// a few past 3 s), and Go's cutoff is fixed at `scheduled_close_at + 5 s`: the
// chips on those rounds are refused with `scheduled_close_exceeded` and the
// round settles empty. The database cannot show it — `event_timestamp` is the
// engine's INTENDED time, not when the row landed.
//
// The backstop is armed on `race_ready` from two stamps that are both the
// engine's own clock — race_ready's `event_timestamp` and the round's
// `scheduled_close_at` — so a client clock that is minutes off changes nothing;
// only the client's lag on the race_ready row itself shifts it, and later. It
// fires at close + the submit timer + a grace, comfortably inside Go's window,
// and is a no-op once the timer path has submitted (`submissionStartedRef`).
import { SUBMIT_TIMER_MS } from 'src/components/ChipPlacementPanel/submissionLifecycle'

/** Headroom past the submit timer's own moment, so an on-time `race_starting` always wins. */
export const SUBMIT_BACKSTOP_GRACE_MS = 1000

/**
 * No Birdie window is anywhere near this long; a gap this wide means the two
 * stamps were not on the same clock (one naive, one zoned) and the backstop
 * must not be armed from them.
 */
export const SUBMIT_BACKSTOP_MAX_WINDOW_MS = 120_000

/**
 * Milliseconds from now until the backstop should submit, or `undefined` when
 * the round gives it nothing safe to arm from (no odds yet, an unparseable
 * stamp, a close already behind the ready stamp, an implausible window).
 */
export function submitBackstopDelayMs(input: {
  readyTimestamp: string | undefined
  scheduledCloseAt: string | undefined
}): number | undefined {
  if (!input.readyTimestamp || !input.scheduledCloseAt) return undefined
  const readyMs = Date.parse(input.readyTimestamp)
  const closeMs = Date.parse(input.scheduledCloseAt)
  if (!Number.isFinite(readyMs) || !Number.isFinite(closeMs)) return undefined
  const windowMs = closeMs - readyMs
  if (windowMs <= 0 || windowMs > SUBMIT_BACKSTOP_MAX_WINDOW_MS) return undefined
  return windowMs + SUBMIT_TIMER_MS + SUBMIT_BACKSTOP_GRACE_MS
}
